import React from 'react';
import styled from 'styled-components';

const StyledHeroSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  justify-content: space-between;
  margin-top: 0;
  padding: 80px 50px;
  min-height: 650px;
  background: linear-gradient(135deg, #f0f4f9 0%, #e8f0f7 100%);
  position: relative;
  overflow: hidden;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 60px 40px;
    min-height: auto;
  }

  @media (max-width: 768px) {
    padding: 40px 25px;
    gap: 30px;
  }

  @media (max-width: 480px) {
    padding: 30px 15px;
    gap: 20px;
    min-height: auto;
  }

  /* Decorative background elements */
  &::before {
    content: '';
    position: absolute;
    top: -100px;
    right: -100px;
    width: 400px;
    height: 400px;
    background: rgba(0, 153, 255, 0.08);
    border-radius: 50%;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: rgba(0, 153, 255, 0.05);
    border-radius: 50%;
    z-index: 0;
  }

  /* Left side content */
  .hero-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    z-index: 1;

    @media (max-width: 1080px) {
      order: 2;
    }
  }

  /* Accent line like ConnectMIT */
  .accent-line {
    font-size: var(--fz-xs);
    letter-spacing: 0.2em;
    color: #0099ff;
    text-transform: uppercase;
    font-weight: 700;
    margin-bottom: 25px;
    text-decoration: none;
    display: block;

    @media (max-width: 600px) {
      margin-bottom: 15px;
      font-size: 10px;
    }
  }

  h1 {
    font-size: clamp(36px, 8vw, 64px);
    font-weight: 800;
    color: #001f3f;
    margin: 0 0 30px 0;
    line-height: 1.15;
    text-transform: uppercase;
    letter-spacing: 0.015em;

    @media (max-width: 600px) {
      font-size: clamp(28px, 6vw, 42px);
      margin-bottom: 20px;
    }
  }

  .description {
    max-width: 550px;
    font-family: var(--font-sans);
    font-size: 16px;
    line-height: 1.7;
    color: #4a6fa5;
    margin-bottom: 50px;
    font-weight: 500;

    @media (max-width: 768px) {
      font-size: 15px;
      line-height: 1.6;
      margin-bottom: 35px;
    }

    @media (max-width: 480px) {
      font-size: 14px;
      line-height: 1.5;
      margin-bottom: 25px;
    }
  }

  .cta-buttons {
    display: flex;
    gap: 20px;
    align-items: center;
    flex-wrap: wrap;

    @media (max-width: 600px) {
      flex-direction: column;
      gap: 15px;
    }
  }

  .cta-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 28px;
    border: none;
    color: white;
    text-decoration: none;
    font-family: var(--font-sans);
    font-size: 13px;
    letter-spacing: 0.1em;
    border-radius: 30px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    font-weight: 700;
    background: linear-gradient(135deg, #0099ff 0%, #0077cc 100%);
    box-shadow: 0 4px 15px rgba(0, 153, 255, 0.3);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(0, 153, 255, 0.4);
      background: linear-gradient(135deg, #00aaff 0%, #0088dd 100%);
    }

    &:active {
      transform: scale(0.98);
    }

    @media (max-width: 600px) {
      padding: 12px 24px;
      font-size: 12px;
      width: 100%;
    }
  }

  .cta-button.secondary {
    background: transparent;
    color: #0099ff;
    border: 2px solid #0099ff;
    box-shadow: none;

    &:hover {
      background: #0099ff;
      color: white;
      box-shadow: 0 8px 25px rgba(0, 153, 255, 0.3);
    }
  }

  /* Right side hero image */
  .hero-image {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    order: 1;
    z-index: 1;

    @media (max-width: 1080px) {
      order: 1;
      min-height: 400px;
    }

    @media (max-width: 768px) {
      min-height: 300px;
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: 12px;
      filter: drop-shadow(0 15px 35px rgba(0, 0, 0, 0.15));
    }

    /* Decorative accent elements */
    &::before {
      content: '';
      position: absolute;
      width: 200px;
      height: 200px;
      background: rgba(0, 153, 255, 0.08);
      border-radius: 50%;
      bottom: -80px;
      right: -80px;
      z-index: -1;
    }

    &::after {
      content: '';
      position: absolute;
      width: 100px;
      height: 100px;
      background: rgba(0, 153, 255, 0.06);
      border-radius: 50%;
      top: -30px;
      left: -30px;
      z-index: -1;
    }
  }
`;

const Hero = () => (
  <StyledHeroSection id="hero">
    <div className="hero-content">
      <div className="accent-line">TRANSFORMING BUSINESSES THROUGH TECHNOLOGY</div>

      <h1>Connect Your Business to the IT World</h1>

      <p className="description">
        Draa Talents is a leading IT consulting firm specializing in innovative tech solutions,
        cloud infrastructure, AI technology, and seamless system integration. Empowering businesses
        with expert talent and cutting-edge solutions.
      </p>

      <div className="cta-buttons">
        <a href="#services" className="cta-button">
          ➜ Our Services
        </a>
        <a href="#contact" className="cta-button secondary">
          Get In Touch
        </a>
      </div>
    </div>

    <div className="hero-image">
      <svg viewBox="0 0 450 550" style={{ width: '100%', maxWidth: '500px', height: 'auto' }}>
        {/* Professional figure illustration */}
        <defs>
          <linearGradient id="skinGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#F4A460', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#DEB887', stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id="jacketGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#8B9467', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#6B7647', stopOpacity: 1 }} />
          </linearGradient>
        </defs>

        {/* Head */}
        <ellipse cx="225" cy="100" rx="45" ry="50" fill="url(#skinGradient)" />

        {/* Hair */}
        <path
          d="M 180 80 Q 180 40 225 40 Q 270 40 270 80 Q 270 100 225 110 Q 180 100 180 80"
          fill="#2C1810"
        />

        {/* Face features */}
        <circle cx="210" cy="95" r="5" fill="#000" />
        <circle cx="240" cy="95" r="5" fill="#000" />
        <path d="M 210 110 Q 225 120 240 110" stroke="#000" strokeWidth="2" fill="none" />

        {/* Jacket */}
        <path
          d="M 160 160 Q 160 200 155 280 L 160 350 L 295 350 L 300 280 Q 300 200 290 160 Z"
          fill="url(#jacketGradient)"
        />

        {/* Shirt underneath */}
        <path d="M 180 160 L 270 160 L 265 280 L 185 280 Z" fill="#FFFFFF" />

        {/* Left arm */}
        <g>
          <path
            d="M 160 180 L 120 240"
            stroke="url(#skinGradient)"
            strokeWidth="32"
            strokeLinecap="round"
          />
          <ellipse cx="110" cy="250" rx="18" ry="20" fill="url(#skinGradient)" />
        </g>

        {/* Right arm - raised */}
        <g>
          <path
            d="M 290 180 Q 320 170 340 150"
            stroke="url(#skinGradient)"
            strokeWidth="32"
            strokeLinecap="round"
          />
          <ellipse cx="350" cy="140" rx="18" ry="20" fill="url(#skinGradient)" />
        </g>

        {/* Legs - dark pants */}
        <g>
          <rect x="185" y="350" width="25" height="130" fill="#1a1a2e" rx="5" />
          <rect x="240" y="350" width="25" height="130" fill="#1a1a2e" rx="5" />

          {/* Shoes */}
          <ellipse cx="197" cy="490" rx="22" ry="18" fill="#000" />
          <ellipse cx="252" cy="490" rx="22" ry="18" fill="#000" />
        </g>

        {/* Decorative office background elements */}
        <g opacity="0.3">
          {/* Shelf/Cabinet */}
          <rect
            x="50"
            y="200"
            width="80"
            height="150"
            fill="none"
            stroke="#999"
            strokeWidth="2"
            rx="4"
          />
          <line x1="50" y1="240" x2="130" y2="240" stroke="#999" strokeWidth="1" />
          <line x1="50" y1="280" x2="130" y2="280" stroke="#999" strokeWidth="1" />
          <line x1="50" y1="320" x2="130" y2="320" stroke="#999" strokeWidth="1" />
        </g>

        {/* Decorative tech elements */}
        <g opacity="0.2">
          <circle cx="380" cy="150" r="40" fill="none" stroke="#0099ff" strokeWidth="2" />
          <circle cx="380" cy="150" r="30" fill="none" stroke="#0099ff" strokeWidth="1" />
        </g>
      </svg>
    </div>
  </StyledHeroSection>
);

export default Hero;
